import type { Bot, BotChat, Group, RunActivityRow } from "@rakazo/contracts";
import { isActive } from "@rakazo/core";
import type { ActivityListFilters } from "./activity-list-filters";
import { filterActivityRuns } from "./activity-list-filters";

export const RECENT_ACTIVITY_CHAT_LIMIT = 8;

export type ActivityChatKey = string;

/** One sidebar row per group thread or direct/extra bot thread. */
export function activityChatKey(run: RunActivityRow): ActivityChatKey {
  if (run.groupId) return `g:${run.groupId}`;
  return `t:${run.threadId}`;
}

export type ActivityChatRow = {
  key: ActivityChatKey;
  latestRun: RunActivityRow;
  activeRun: RunActivityRow | null;
};

export type ActivityChatIndicator = "active" | "approval" | "unread";

export function activityChatIndicator(
  activeRun: RunActivityRow | null,
  unread: boolean,
): ActivityChatIndicator | null {
  if (activeRun) {
    if (activeRun.status === "waiting_input" || activeRun.status === "waiting_takeover") {
      return "approval";
    }
    if (isActive(activeRun.status)) return "active";
  }
  if (unread) return "unread";
  return null;
}

function mergeLatestRun(map: Map<ActivityChatKey, RunActivityRow>, run: RunActivityRow): void {
  const key = activityChatKey(run);
  const existing = map.get(key);
  if (!existing || Date.parse(run.updatedAt) >= Date.parse(existing.updatedAt)) {
    map.set(key, run);
  }
}

/** Collapse runs to the newest row per chat, newest chats first. */
export function groupActivityRuns(
  runs: readonly RunActivityRow[],
  activeRuns: readonly RunActivityRow[] = [],
): ActivityChatRow[] {
  const latestByChat = new Map<ActivityChatKey, RunActivityRow>();
  for (const run of runs) mergeLatestRun(latestByChat, run);
  for (const run of activeRuns) mergeLatestRun(latestByChat, run);

  const activeByChat = new Map<ActivityChatKey, RunActivityRow>();
  for (const run of activeRuns) {
    const key = activityChatKey(run);
    if (!activeByChat.has(key)) activeByChat.set(key, run);
  }

  const keys = new Set([...latestByChat.keys(), ...activeByChat.keys()]);
  return [...keys]
    .map((key) => ({
      key,
      latestRun: latestByChat.get(key)!,
      activeRun: activeByChat.get(key) ?? null,
    }))
    .sort(
      (left, right) => Date.parse(right.latestRun.updatedAt) - Date.parse(left.latestRun.updatedAt),
    );
}

export function filterAndGroupActivityChats(
  recentRuns: readonly RunActivityRow[],
  activeRuns: readonly RunActivityRow[],
  filters: ActivityListFilters,
): ActivityChatRow[] {
  const filteredRecent = filterActivityRuns([...recentRuns], filters);
  const filteredActive = filterActivityRuns([...activeRuns], filters);
  return groupActivityRuns(filteredRecent, filteredActive);
}

export type ActivityUnreadLookup = {
  bots: readonly Pick<Bot, "id" | "threadId" | "unread">[];
  groups: readonly Pick<Group, "id" | "unread">[];
  extraChats: readonly Pick<BotChat, "threadId" | "unread">[];
};

export function activityChatUnread(run: RunActivityRow, lookup: ActivityUnreadLookup): boolean {
  if (run.groupId) {
    return lookup.groups.find((group) => group.id === run.groupId)?.unread ?? false;
  }
  const extra = lookup.extraChats.find((chat) => chat.threadId === run.threadId);
  if (extra) return extra.unread;
  const bot = lookup.bots.find((candidate) => candidate.id === run.botId);
  if (!bot) return false;
  if (bot.threadId === run.threadId) return bot.unread;
  return false;
}

export function activityChatPath(run: RunActivityRow, lookup: ActivityUnreadLookup): string {
  if (run.groupId) return `/app/g/${run.groupId}`;
  const bot = lookup.bots.find((candidate) => candidate.id === run.botId);
  if (bot && bot.threadId !== run.threadId) {
    return `/app/${run.botId}/c/${run.threadId}`;
  }
  return `/app/${run.botId}`;
}
